import {
  Box, Button, Checkbox, IconButton, ListItemIcon, Menu, MenuItem, Pagination, Typography, useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { differenceInCalendarDays, isValid, parseISO } from 'date-fns'
import {
  Fragment, MouseEvent, ReactNode, useEffect, useMemo, useState
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { IFolder, ListSummary, QuickFilter } from '../../../../api/FolderApi'
import { TOKENS } from '../../../../pages/dashboard-v2/components/primitives'
import { FolderChecklist } from '../../../../types/FolderChecklist'
import { IDataTableAction } from '../../../widgets/DataTableHeader'
import {
  AssigneesCell, DateCell, DueCell, HintsCell, ProgressCell, ProgressRing, TitleCell
} from './NewDesignCells'

// The process list of the new design (redesign round 1, design draft screens 1, 2 and 6).
// Data, actions, selection, sorting and paging come from Folder.tsx exactly as for the old table:
// `rows` are the old table rows (ids, types, status, used by the actions), `raw` the list entries.

interface MultiSelectAction {
  label: string
  callback: (items: any[]) => void
  show?: boolean
  isDanger?: boolean
}

interface Props {
  rows: any[]
  raw: FolderChecklist[]
  status: 'open' | 'closed' | 'due' | 'invited'
  tabIndex: number
  folder: IFolder
  offlineIds: string[]
  summary: ListSummary | null
  quick: QuickFilter | null
  onQuick: (quick: QuickFilter | null) => void
  actions: Array<IDataTableAction<any>>
  multiSelectActions: MultiSelectAction[]
  sortColumn: string
  sortDir: string
  onSort: (field: string, direction: 'asc' | 'desc') => void
  page: number
  totalPages: number
  onPagination: (page: number) => void
  selectedId?: string
  onOpenFolder: (id: string) => void
  isLoading: boolean
  // path of the current folder (only inside a folder) and the buttons "Neue Vorlage" / "Prozess starten"
  breadcrumb?: ReactNode
  toolbar?: ReactNode
}

type Bucket = 'overdue' | 'today' | 'week' | 'later'

const BUCKET_STYLE: Record<Bucket, { dot: string, color: string }> = {
  overdue: { dot: '#C62828', color: '#A3261F' },
  today: { dot: '#B26A00', color: '#8A5300' },
  week: { dot: TOKENS.primary, color: TOKENS.primaryDark },
  later: { dot: '#8A94A6', color: TOKENS.muted }
}

function bucketOf(due?: string | null): Bucket {
  if (!due) return 'later'
  const date = parseISO(String(due))
  if (!isValid(date)) return 'later'
  const now = new Date()
  if (date < now) return 'overdue'
  const days = differenceInCalendarDays(date, now)
  if (days === 0) return 'today'
  if (days <= 7) return 'week'
  return 'later'
}

function linkTo(x: FolderChecklist, closed: boolean, tabIndex: number, folder: IFolder) {
  return closed
    ? `/checklists/${x.checklist_id}/audit/${x.id}/report`
    // opens next to the list (drafts 1b / 1c); the page (draft 3) via the panel's expand button
    : `/checklists/tab/${tabIndex}/folders/${folder.id}/${x.checklist_id}/audit/${x.id}`
}

export function RowMenu({
  row, actions, small = false, anchorEl, onAnchor, label
}: {
  row: any, actions: Array<IDataTableAction<any>>, small?: boolean
  // controlled anchor (folder chips open the same menu on right-click)
  anchorEl?: HTMLElement | null, onAnchor?: (el: HTMLElement | null) => void, label?: string
}) {
  const { t } = useTranslation()
  const [ownAnchor, setOwnAnchor] = useState<HTMLElement | null>(null)
  const anchor = onAnchor ? (anchorEl ?? null) : ownAnchor
  const setAnchor = onAnchor ?? setOwnAnchor
  const valid = actions.filter((a) => !a.isValidForItem || a.isValidForItem(row))
  if (!valid.length) return small ? null : <Box sx={{ width: 36 }} />
  return (
    <>
      <IconButton
        aria-label={label ?? t('processList.actions')}
        aria-haspopup="menu"
        onClick={(event: MouseEvent<HTMLElement>) => { event.stopPropagation(); setAnchor(event.currentTarget) }}
        sx={small
          ? { width: 24, height: 24, borderRadius: '999px', color: TOKENS.muted, ml: 0.25, mr: -0.75 }
          : { width: 36, height: 36, borderRadius: '8px', color: TOKENS.muted }}
      >
        <i className={small ? 'fas fa-angle-down' : 'fas fa-ellipsis-h'} style={{ fontSize: small ? 12 : 16 }} />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} onClick={(e) => e.stopPropagation()}>
        {valid.map((action) => {
          const disabled = !!action.isDisabledForItem && action.isDisabledForItem(row)
          const danger = action.className?.includes('is-danger')
          return (
            <MenuItem
              key={`${action.icon}-${action.tooltip}`}
              disabled={disabled}
              onClick={() => { setAnchor(null); action.onClick(row) }}
              sx={{ fontSize: 14, color: danger ? '#A3261F' : TOKENS.text, minHeight: 40 }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 30 }}>
                <i className={action.icon.replace('has-pointer', '')} style={{ fontSize: 14 }} />
              </ListItemIcon>
              {action.tooltip}
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

// subfolders as chips, alphabetical; the small arrow (or a right-click) opens the folder actions
// of the old table (rename, move, delete, ...), the chip itself opens the folder
export function FolderChips({
  folders, closed, actions, onOpenFolder, count, phone
}: {
  folders: Array<{ x: FolderChecklist, row: any }>, closed: boolean, actions: Array<IDataTableAction<any>>
  onOpenFolder: (id: string) => void
  // what the number on a chip counts (default: the processes of this tab; templates: template_count)
  count?: (folder: FolderChecklist) => number
  // phones: the chips hide behind one "Ordner" chip until it is tapped
  phone?: boolean
}) {
  const { t } = useTranslation()
  const [menu, setMenu] = useState<{ id: string, el: HTMLElement } | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [open, setOpen] = useState(false)
  const countOf = (f: FolderChecklist) => (count
    ? count(f)
    : Number((closed ? f.closed_checklist_count : f.open_checklist_count) ?? 0))
  // many folders (BRAWO: 20+): the ones with processes in this tab first, the rest behind one chip
  const withItems = folders.filter(({ x }) => countOf(x) > 0)
  const without = folders.filter(({ x }) => countOf(x) === 0)
  // only when some folders stay visible; otherwise all of them would hide behind the chip
  const canCollapse = without.length > 4 && withItems.length > 0
  const collapse = canCollapse && !showAll
  const visible = collapse ? withItems : [...withItems, ...without]
  const toggle = phone && (
    <Box
      component="button"
      type="button"
      onClick={() => setOpen((v) => !v)}
      aria-expanded={open}
      sx={{
        all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 30, px: 1.5,
        borderRadius: '999px', border: '1px solid #C3CCD8', bgcolor: open ? '#F4F6F9' : '#FFFFFF', color: TOKENS.text, fontSize: 13, fontWeight: 600
      }}
    >
      <i className="fas fa-folder" style={{ fontSize: 12, color: TOKENS.primary }} aria-hidden />
      {t('processList.folderToggle')}
      <Box component="span" sx={{ color: TOKENS.muted, fontWeight: 500 }}>{folders.length}</Box>
      <i className={open ? 'fas fa-chevron-up' : 'fas fa-chevron-down'} style={{ fontSize: 10, color: TOKENS.muted }} aria-hidden />
    </Box>
  )
  if (phone && !open) return <Box>{toggle}</Box>
  return (
    <Box sx={{
      display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap'
    }}
    >
      {toggle || <Typography sx={{ fontSize: 13, color: TOKENS.muted }}>{t('processList.folders')}</Typography>}
      {visible.map(({ x: f, row }) => {
        const count = countOf(f)
        // empty folders (for this tab) step back: grey icon and lighter text
        const empty = count === 0
        return (
        <Box
          key={f.id}
          onContextMenu={(e: MouseEvent<HTMLElement>) => { e.preventDefault(); setMenu({ id: f.id, el: e.currentTarget }) }}
          sx={{
            display: 'inline-flex', alignItems: 'center', height: 30, pl: 1.5, pr: 1.25, borderRadius: '999px',
            border: '1px solid #C3CCD8', bgcolor: '#FFFFFF', '&:hover': { bgcolor: '#F4F6F9' }
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => onOpenFolder(f.id)}
            sx={{
              all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.75, color: empty ? TOKENS.muted : TOKENS.text, fontSize: 13, fontWeight: 600
            }}
          >
            <i className={empty ? 'far fa-folder' : 'fas fa-folder'} style={{ fontSize: 12, color: empty ? '#8A94A6' : TOKENS.primary }} aria-hidden />
            {f.title}
            <Box component="span" sx={{ color: TOKENS.muted, fontWeight: 500 }}>
              {count}
            </Box>
          </Box>
          <RowMenu
            row={row}
            actions={actions}
            small
            label={t('processList.folderActions', { name: f.title })}
            anchorEl={menu?.id === f.id ? menu.el : null}
            onAnchor={(el) => setMenu(el ? { id: f.id, el } : null)}
          />
        </Box>
        )
      })}
      {canCollapse && (
        <Box
          component="button"
          type="button"
          onClick={() => setShowAll((v) => !v)}
          aria-expanded={!collapse}
          sx={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            height: 30,
            px: 1.5,
            borderRadius: '999px',
            border: '1px dashed #C3CCD8',
            color: TOKENS.muted,
            fontSize: 13,
            fontWeight: 600,
            '&:hover': { bgcolor: '#F4F6F9' }
          }}
        >
          <i className={collapse ? 'far fa-folder' : 'fas fa-chevron-up'} style={{ fontSize: 12 }} aria-hidden />
          {collapse ? t('processList.moreFolders', { count: without.length }) : t('processList.fewerFolders')}
        </Box>
      )}
    </Box>
  )
}

export function QuickChip({
  label, count, active, dot, onClick
}: { label: string, count?: number | null, active: boolean, dot?: string, onClick: () => void }) {
  return (
    <Button
      onClick={onClick}
      aria-pressed={active}
      sx={{
        flex: 'none',
        height: 36,
        px: 1.75,
        borderRadius: '999px',
        border: '1px solid',
        borderColor: active ? TOKENS.text : '#C3CCD8',
        bgcolor: active ? TOKENS.text : '#FFFFFF',
        color: active ? '#FFFFFF' : TOKENS.text,
        fontSize: 13,
        fontWeight: 600,
        textTransform: 'none',
        gap: 1,
        '&:hover': { bgcolor: active ? TOKENS.text : '#F4F6F9' }
      }}
    >
      {dot && <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot }} />}
      {label}
      {count !== undefined && count !== null && <span>{count}</span>}
    </Button>
  )
}

export default function ProcessListV2(props: Props) {
  const {
    rows, raw, status, tabIndex, folder, offlineIds, summary, quick, onQuick, actions, multiSelectActions,
    sortColumn, sortDir, onSort, page, totalPages, onPagination, selectedId, onOpenFolder, isLoading,
    breadcrumb, toolbar
  } = props
  const { t } = useTranslation()
  const history = useHistory()
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))
  // desktop: the buttons sit on the line of the tabs (slot rendered by ProcessOverview)
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [tabSlot, setTabSlot] = useState<HTMLElement | null>(null)
  useEffect(() => { setTabSlot(document.getElementById('nd-tab-actions')) }, [])
  const closed = status === 'closed'
  // a process is open next to the list (drafts 1b / 1c): compact rows without columns
  const compact = !!selectedId && !isPhone
  const [selected, setSelected] = useState<string[]>([])

  useEffect(() => { setSelected([]) }, [raw])

  const folders = useMemo(() => raw
    .map((x, index) => ({ x, row: rows[index] }))
    .filter(({ x }) => x.entity_type === 'folder')
    .sort((a, b) => String(a.x.title ?? '').localeCompare(String(b.x.title ?? ''), 'de', { numeric: true, sensitivity: 'base' })),
  [raw, rows])
  const processes = useMemo(() => raw
    .map((x, index) => ({ x, row: rows[index] }))
    .filter(({ x }) => x.entity_type !== 'folder'), [raw, rows])

  // groups by due date (Überfällig, Heute, Diese Woche, Später) for open processes, whatever the
  // sort order (users bring theirs from the old design) and with quick filters too
  const grouped = !closed && status !== 'invited'
  const groups = useMemo(() => {
    if (!grouped) return [{ key: null as Bucket | null, items: processes }]
    // most urgent first, like the draft; inside a group the chosen sort order stays
    const order: Bucket[] = ['overdue', 'today', 'week', 'later']
    return order.map((key) => ({ key, items: processes.filter(({ x }) => bucketOf(x.due_date) === key) }))
      .filter((g) => g.items.length > 0)
  }, [grouped, processes])

  const toggle = (id: string) => setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  const selectedRows = rows.filter((r) => selected.includes(r.id))
  const allIds = processes.map(({ x }) => x.id)

  const sortBy = (field: string) => onSort(field, sortColumn === field && sortDir === 'asc' ? 'desc' : 'asc')
  const sortMark = (field: string) => (sortColumn === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '')

  // "Hinweise" (deviations, open tasks, offline, ready to close) only when a row on this page has one
  const showHints = processes.some(({ x }) => x.deviation_count || x.open_task_count
    || offlineIds.includes(x.id) || x.locked || (x.progress ?? 0) >= 100)
  const columns = closed
    ? 'minmax(0,1fr) 120px 150px 100px 150px 200px 44px'
    : `minmax(0,1fr) 140px 180px 150px ${showHints ? '230px ' : ''}44px`
  const gridColumns = `28px ${columns}`

  const header = (
    <Box sx={{
      display: { xs: 'none', md: 'grid' },
      gridTemplateColumns: gridColumns,
      gap: 2,
      alignItems: 'center',
      px: 2.5,
      py: 1.25,
      bgcolor: '#F7F9FC',
      fontSize: 12,
      fontWeight: 600,
      color: TOKENS.muted
    }}
    >
      <Checkbox
        size="small"
        sx={{ p: 0 }}
        inputProps={{ 'aria-label': t('processList.selectAll') }}
        checked={allIds.length > 0 && allIds.every((id) => selected.includes(id))}
        indeterminate={selected.length > 0 && !allIds.every((id) => selected.includes(id))}
        onChange={(e) => setSelected(e.target.checked ? allIds : [])}
      />
      <Box component="button" type="button" onClick={() => sortBy('title')} sx={headerButton}>{t('processList.process')}{sortMark('title')}</Box>
      {closed ? (
        <>
          <span>{t('processList.responsible')}</span>
          <Box component="button" type="button" onClick={() => sortBy('closed_at')} sx={headerButton}>{t('processList.closedAt')}{sortMark('closed_at')}</Box>
          <span>{t('resultSet.duration')}</span>
          <span>{t('processList.progress')}</span>
          <span>{t('processList.result')}</span>
        </>
      ) : (
        <>
          <span>{t('processList.responsible')}</span>
          <Box component="button" type="button" onClick={() => sortBy('due_date')} sx={headerButton}>{t('processList.due')}{sortMark('due_date')}</Box>
          <span>{t('processList.progress')}</span>
          {showHints && <span>{t('processList.hints')}</span>}
        </>
      )}
      <span />
    </Box>
  )

  const renderRow = ({ x, row }: { x: FolderChecklist, row: any }) => {
    const to = linkTo(x, closed, tabIndex, folder)
    const sub = closed
      ? [x.closed_by_name, x.project].filter(Boolean).join(' · ')
      : [x.project, x.resource].filter(Boolean).join(' · ')
    const offline = offlineIds.includes(x.id)
    const hints = (
      <HintsCell closed={closed} locked={x.locked} deviations={x.deviation_count} tasks={x.open_task_count} offline={offline} progress={x.progress} />
    )
    const isSelected = selectedId === x.id
    const open = () => history.push(to)

    if (compact) {
      const devs = x.deviation_count ?? 0
      // counts behind the progress (visible questions); before the cache has them, the percentage
      const progressText = closed ? '' : (x.visible_question_count
        ? t('processList.answered', { answered: x.visible_result_count ?? 0, total: x.visible_question_count })
        : `${Math.round(x.progress ?? 0)} %`)
      return (
        <Box
          key={x.id}
          onClick={open}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderTop: `1px solid ${TOKENS.divider}`, cursor: 'pointer',
            bgcolor: isSelected ? '#EEF4FC' : '#FFFFFF',
            boxShadow: isSelected ? `inset 0 0 0 2px ${TOKENS.primary}` : 'none',
            '&:hover': { bgcolor: isSelected ? '#EEF4FC' : '#F7F9FC' }
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TitleCell
              title={x.title}
              to={to}
              sub={[progressText || null, closed ? x.closed_by_name : null].filter(Boolean).join(' · ') || sub}
              offline={offline}
              extra={devs > 0 ? (
                <Typography sx={{ fontSize: 12, color: '#A3261F', fontWeight: 600 }}>{t('processList.deviations', { count: devs })}</Typography>
              ) : null}
            />
          </Box>
          <Box sx={{ flex: 'none', textAlign: 'right', '& p:last-of-type': { display: 'none' } }}>
            {closed ? <DateCell value={x.closed_at} /> : <DueCell due={x.due_date} overdue={x.is_over_due} short />}
          </Box>
          {!closed && <ProgressRing progress={x.progress} />}
        </Box>
      )
    }

    if (isPhone) {
      return (
        <Box
          key={x.id}
          onClick={open}
          sx={{
            display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.75, bgcolor: '#FFFFFF', border: '1px solid',
            borderColor: isSelected ? TOKENS.primary : '#C3CCD8', borderRadius: '12px', cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(16,24,40,.10)'
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TitleCell title={x.title} to={to} sub={sub} offline={offline} />
            </Box>
            <Box onClick={(e) => e.stopPropagation()}><RowMenu row={row} actions={actions} /></Box>
          </Box>
          {/* the date sits on the progress line, so long titles keep the full card width */}
          {closed ? (
            <Box sx={{ '& p:last-of-type': { display: 'none' } }}><DateCell value={x.closed_at} /></Box>
          ) : (
            <ProgressCell
              progress={x.progress}
              answered={x.visible_result_count}
              total={x.visible_question_count}
              aside={<Box sx={{ textAlign: 'right', '& p:last-of-type': { display: 'none' } }}><DueCell due={x.due_date} overdue={x.is_over_due} /></Box>}
            />
          )}
          {hints}
        </Box>
      )
    }

    return (
      <Box
        key={x.id}
        onClick={open}
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          gap: 2,
          alignItems: 'center',
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${TOKENS.divider}`,
          bgcolor: isSelected ? '#EEF4FC' : '#FFFFFF',
          cursor: 'pointer',
          '&:hover': { bgcolor: isSelected ? '#EEF4FC' : '#F7F9FC' }
        }}
      >
        <Checkbox
          size="small"
          sx={{ p: 0 }}
          checked={selected.includes(x.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggle(x.id)}
          inputProps={{ 'aria-label': t('processList.select', { title: x.title }) }}
        />
        <TitleCell title={x.title} to={to} sub={sub} offline={offline} />
        {closed ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}><AssigneesCell assignees={x.assignees} responsible={x.responsible} /></Box>
            <DateCell value={x.closed_at} />
            <Typography sx={{ fontSize: 13.5, color: TOKENS.muted }}>{x.duration ?? ''}</Typography>
            <ProgressCell progress={x.progress} answered={x.visible_result_count} total={x.visible_question_count} />
            {hints}
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}><AssigneesCell assignees={x.assignees} responsible={x.responsible} /></Box>
            <DueCell due={x.due_date} overdue={x.is_over_due} />
            <ProgressCell progress={x.progress} answered={x.visible_result_count} total={x.visible_question_count} />
            {showHints && hints}
          </>
        )}
        <Box onClick={(e) => e.stopPropagation()}><RowMenu row={row} actions={actions} /></Box>
      </Box>
    )
  }

  const groupHeader = (key: Bucket) => {
    const style = BUCKET_STYLE[key]
    const count = summary ? summary[key] : null
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, px: { xs: 0.5, md: 2.5 }, pt: 1.75, pb: 1,
        borderTop: { md: `1px solid ${TOKENS.divider}` }, bgcolor: { md: '#FFFFFF' }
      }}
      >
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: style.dot }} />
        <Typography sx={{
          fontSize: 12.5, fontWeight: 700, letterSpacing: '.3px', color: style.color, textTransform: 'uppercase'
        }}
        >
          {t(`processList.group.${key}`)}
        </Typography>
        {count !== null && <Typography sx={{ fontSize: 13, color: TOKENS.muted }}>{count}</Typography>}
      </Box>
    )
  }

  const visibleBulk = multiSelectActions.filter((a) => a.show !== false)
  const showQuick = status !== 'invited' && !!summary
  const toolbarInTabs = !!toolbar && !compact && !!tabSlot && isDesktop
  // without quick filters, folders or buttons the row would only leave a gap
  const showHeadRow = showQuick || (folders.length > 0 && !compact) || (!!toolbar && !compact && !toolbarInTabs)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* one row: quick filters (or, without them, the folders) left, the buttons right; no extra rows */}
      {breadcrumb}
      {toolbarInTabs && createPortal(toolbar, tabSlot as HTMLElement)}
      {showHeadRow && (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minHeight: 40
      }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {showQuick ? (
        <Box sx={{
          display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: compact ? 'wrap' : { xs: 'nowrap', md: 'wrap' }, overflowX: 'auto', pb: 0.5
        }}
        >
          <QuickChip label={t('processList.quick.all')} count={summary?.total} active={!quick} onClick={() => onQuick(null)} />
          <QuickChip label={t('processList.quick.mine')} count={summary?.mine} active={quick === 'mine'} onClick={() => onQuick('mine')} />
          {closed ? (
            <>
              <QuickChip label={t('processList.quick.closedToday')} count={summary?.closed_today} active={quick === 'closed_today'} onClick={() => onQuick('closed_today')} />
              <QuickChip label={t('processList.quick.closedWeek')} count={summary?.closed_week} active={quick === 'closed_week'} onClick={() => onQuick('closed_week')} />
              <QuickChip label={t('processList.quick.closedMonth')} count={summary?.closed_month} active={quick === 'closed_month'} onClick={() => onQuick('closed_month')} />
              <QuickChip label={t('processList.quick.deviation')} count={summary?.deviation} dot="#C62828" active={quick === 'deviation'} onClick={() => onQuick('deviation')} />
              <QuickChip label={t('processList.quick.noDeviation')} count={summary?.no_deviation} dot="#1E7A4C" active={quick === 'no_deviation'} onClick={() => onQuick('no_deviation')} />
            </>
          ) : (
            <>
              <QuickChip label={t('processList.quick.overdue')} count={summary?.overdue} dot="#C62828" active={quick === 'overdue'} onClick={() => onQuick('overdue')} />
              <QuickChip label={t('processList.quick.today')} count={summary?.today} dot="#B26A00" active={quick === 'today'} onClick={() => onQuick('today')} />
              <QuickChip label={t('processList.quick.deviation')} count={summary?.deviation} active={quick === 'deviation'} onClick={() => onQuick('deviation')} />
            </>
          )}
        </Box>
          ) : (folders.length > 0 && !compact && (
        <FolderChips folders={folders} closed={closed} actions={actions} onOpenFolder={onOpenFolder} phone={isPhone} />
          ))}
        </Box>
        {toolbar && !compact && !toolbarInTabs && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, flex: 'none'
          }}
          >
            {toolbar}
          </Box>
        )}
      </Box>
      )}
      {showQuick && folders.length > 0 && !compact && (
        <FolderChips folders={folders} closed={closed} actions={actions} onOpenFolder={onOpenFolder} phone={isPhone} />
      )}

      {selected.length > 0 && visibleBulk.length > 0 && (
        <Box sx={{
          display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', p: 1.25, borderRadius: '10px', bgcolor: TOKENS.primarySoft
        }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, mr: 1 }}>{t('processList.selected', { count: selected.length })}</Typography>
          {visibleBulk.map((a) => (
            <Button
              key={a.label}
              size="small"
              variant="outlined"
              color={a.isDanger ? 'error' : 'primary'}
              onClick={() => a.callback(selectedRows)}
              sx={{ textTransform: 'none', bgcolor: '#FFFFFF' }}
            >
              {a.label}
            </Button>
          ))}
          <Button size="small" onClick={() => setSelected([])} sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
        </Box>
      )}

      {isPhone ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {groups.map((g) => (
            <Fragment key={g.key ?? 'all'}>
              {g.key && groupHeader(g.key)}
              {g.items.map(renderRow)}
            </Fragment>
          ))}
        </Box>
      ) : (
        <Box sx={{
          bgcolor: '#FFFFFF', border: '1px solid #C3CCD8', borderRadius: '12px', overflow: 'hidden', boxShadow: TOKENS.cardShadow
        }}
        >
          {!compact && header}
          {groups.map((g) => (
            <Fragment key={g.key ?? 'all'}>
              {g.key && groupHeader(g.key)}
              {g.items.map(renderRow)}
            </Fragment>
          ))}
        </Box>
      )}

      {!isLoading && processes.length === 0 && (
        <Typography sx={{ color: TOKENS.muted, fontSize: 14, py: 3, textAlign: 'center' }}>{t('datatable.noData')}</Typography>
      )}

      {!isPhone && totalPages > 1 && (
        <Pagination page={page} count={totalPages} onChange={(_, value) => onPagination(value)} shape="rounded" />
      )}
    </Box>
  )
}

export const headerButton = {
  all: 'unset',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  color: TOKENS.muted,
  '&:hover': { color: TOKENS.text }
} as const

